import { createContext, ComponentClass, Component, PropsWithChildren } from 'react';

const Context = createContext<number>(0);

function getDeepestEntry(entries: TitleEntry[]): TitleEntry | undefined {
    const sorted = [...entries].sort((e1, e2) => e1.depth - e2.depth);

    return sorted.pop();
}

function handleTitleChange(title?: string) {
    if (title && title !== document.title) {
        document.title = title;
    }
}

abstract class DocumentTitleBase extends Component<DocumentTitleProps> {
    // noinspection JSUnusedGlobalSymbols
    static contextType = Context;

    private get depth() {
        return this.context;
    }

    private static nextUid = 0;

    // eslint-disable-next-line react/static-property-placement
    declare context: number;

    private uid = DocumentTitleBase.createUid();

    protected abstract store: Store;

    private static createUid() {
        const uid = DocumentTitleBase.nextUid;
        DocumentTitleBase.nextUid += 1;
        return uid;
    }

    componentDidMount() {
        this.updateTitle();
    }

    componentDidUpdate() {
        this.updateTitle();
    }

    componentWillUnmount() {
        this.store.removeTitle(this.uid);
    }

    render() {
        return <Context.Provider value={this.depth + 1}>{this.props.children}</Context.Provider>;
    }

    private updateTitle() {
        this.store.setTitle({
            uid: this.uid,
            title: this.props.title,
            depth: this.depth,
        });
    }
}

class Store {
    private entries: TitleEntry[] = [];

    constructor(private format: FormatFn) {}

    setTitle(titleEntry: TitleEntry) {
        const index = this.entries.findIndex(({ uid }) => uid === titleEntry.uid);
        if (index >= 0) {
            this.entries[index] = titleEntry;
        } else {
            this.entries.push(titleEntry);
        }
        this.emitChange();
    }

    removeTitle(uid: number) {
        const index = this.entries.findIndex(te => te.uid === uid);
        this.entries.splice(index, 1);
        this.emitChange();
    }

    private emitChange() {
        const deepestEntry = getDeepestEntry(this.entries);
        const title = this.format(deepestEntry?.title);
        handleTitleChange(title);
    }
}

function format(title: string | undefined) {
    return title;
}

type Title = string;

interface TitleEntry {
    uid: number;
    title: Title;
    depth: number;
}

interface FormatFn {
    (title: string | undefined): string | undefined;
}

function documentTitle(params: { format?: FormatFn }): ComponentClass<DocumentTitleProps> {
    const { format } = params;

    const store = new Store(format ?? (t => t));

    return class DocumentTitle extends DocumentTitleBase {
        store = store;
    };
}

export interface DocumentTitleProps extends PropsWithChildren<{}> {
    title: Title;
}

export const DocumentTitle = documentTitle({
    format,
});
